import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { Application } from '@modules/common/types/application';
import { type Stitch } from '@modules/common/types/stitch';
import { DynamicPanelService, type DynamicPanelApp } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { type CalendarEvent } from '../types/calendar-event';
import { type ListState } from '../types/list-state';
import { type VirtualFolder } from '../types/virtual-folder';
import { CalendarTenantScopeService } from './calendar-tenant-scope.service';

interface CalendarPanelScope {
  readonly contextId: string | undefined;
  readonly contextType: SpaceType;
  readonly tenantId: string | null;
}

/** Immutable origin captured before the row lookup; valid only for the host that issued it. */
export interface CalendarPanelReturnOrigin extends CalendarPanelScope {
  readonly eventId: string;
  readonly revision: number;
}

interface CalendarPanelReturnTicket {
  readonly origin: CalendarPanelReturnOrigin;
  readonly event: CalendarEvent;
  readonly ownerId: string;
  readonly phase: 'open' | 'closed';
}

/** One List return, owned by a surviving DP host. No rows, dates, drafts or persisted state. */
@Injectable()
export class CalendarPanelReturnStateService {
  private readonly panel = inject(DynamicPanelService);
  private readonly context = inject(SpaceContextStore);
  private readonly calendarScope = inject(CalendarTenantScopeService);
  private readonly destroyRef = inject(DestroyRef);
  private app: DynamicPanelApp;
  private item: Stitch | null = null;
  private observedScope: CalendarPanelScope | null = null;
  private revision = 0;
  private issuedOrigin: CalendarPanelReturnOrigin | null = null;
  private pending: CalendarPanelReturnTicket | null = null;
  private ticket: CalendarPanelReturnTicket | null = null;

  public constructor() {
    this.panel
      .getApp()
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((app) => {
        if (app !== this.app) this.invalidate();
        this.app = app;
      });
    combineLatest([this.context.contextId$, this.context.type$, this.calendarScope.selectedTeamId()])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([contextId, contextType, tenantId]) => {
        const scope = { contextId, contextType, tenantId };
        if (!this.sameScope(this.observedScope, scope)) this.invalidate();
        this.observedScope = scope;
      });
    this.panel
      .getFormState()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ item }) => {
        const previous = this.item;
        const pending = this.pending;
        if (pending && item === pending.event && this.originCurrent(pending.origin) && this.sameEvent(pending, item)) {
          this.issuedOrigin = null;
          this.pending = null;
          this.ticket = pending;
          this.revision++;
        } else if (
          this.ticket?.phase === 'open' &&
          item === null &&
          previous === this.ticket.event &&
          this.sameEvent(this.ticket, previous) &&
          this.scopeCurrent(this.ticket.origin)
        ) {
          this.ticket = { ...this.ticket, phase: 'closed' };
          this.revision++;
        } else if (item !== previous || (this.ticket && item !== null && !this.sameEvent(this.ticket, item))) {
          this.invalidate();
        }
        this.item = item;
      });
  }

  public captureOrigin(
    eventId: string,
    folder: VirtualFolder,
    tab: Exclude<ListState, 'tabs'>,
  ): CalendarPanelReturnOrigin | null {
    if (
      !eventId ||
      folder !== 'all_events' ||
      tab !== 'events' ||
      this.item !== null ||
      this.app !== Application.calendar
    )
      return null;
    const scope = this.liveScope();
    if (!this.sameScope(this.observedScope, scope) || !(scope.tenantId ?? scope.contextId)) return null;
    this.issuedOrigin = Object.freeze({ ...scope, eventId, revision: this.revision });
    return this.issuedOrigin;
  }

  /** Preparing does not arm a return: the service must actually accept this exact fetched Event. */
  public prepareOpening(origin: CalendarPanelReturnOrigin | null, event: CalendarEvent): void {
    if (
      !origin ||
      !this.originCurrent(origin) ||
      event.id !== origin.eventId ||
      event.itemType !== StitchType.EVENT ||
      event.accountId !== (origin.tenantId ?? origin.contextId)
    )
      return;
    this.pending = { origin, event, ownerId: event.accountId, phase: 'open' };
  }

  /** Called at the authoritative folder boundary. The shared Close/Back peek is never consumed or modified. */
  public consume(folder: VirtualFolder): boolean {
    if (!folder) return false;
    const ticket = this.ticket;
    this.ticket = null;
    if (
      !ticket ||
      ticket.phase !== 'closed' ||
      folder !== 'all_events' ||
      this.app !== Application.calendar ||
      this.item !== null ||
      !this.scopeCurrent(ticket.origin)
    )
      return false;
    return this.sameEvent(ticket, this.panel.takeLastClosedFormState()?.item ?? null);
  }

  private liveScope(): CalendarPanelScope {
    return {
      contextId: this.context.contextId(),
      contextType: this.context.type(),
      tenantId: this.calendarScope.effectiveTenantId(),
    };
  }

  private sameScope(a: CalendarPanelScope | null, b: CalendarPanelScope): boolean {
    return !!a && a.contextId === b.contextId && a.contextType === b.contextType && a.tenantId === b.tenantId;
  }

  private scopeCurrent(origin: CalendarPanelReturnOrigin): boolean {
    return this.sameScope(origin, this.liveScope()) && this.sameScope(this.observedScope, origin);
  }

  private originCurrent(origin: CalendarPanelReturnOrigin): boolean {
    return (
      this.issuedOrigin === origin &&
      origin.revision === this.revision &&
      this.item === null &&
      this.app === Application.calendar &&
      this.scopeCurrent(origin)
    );
  }

  private sameEvent(ticket: CalendarPanelReturnTicket, item: Stitch | null): boolean {
    return (
      item === ticket.event &&
      item?.id === ticket.origin.eventId &&
      item.itemType === StitchType.EVENT &&
      item.accountId === ticket.ownerId
    );
  }

  private invalidate(): void {
    this.revision++;
    this.issuedOrigin = null;
    this.pending = null;
    this.ticket = null;
  }
}
